import { Outlet, useLocation, useNavigationType } from "react-router";
import { useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileNavbarTop } from "@/components/layouts/dashboard/components/mobile-navbar-top";
import MobileNavbarBottom from "@/components/layouts/dashboard/components/mobile-navbar-bottom";
import { AudioPlayer } from "@/components/audio/audio-player";
import { useAudio } from "@/app/providers/audio-provider";
import { AppSidebar } from "@/components/layouts/dashboard/components/sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import GetPremiumAlert from "@/components/layouts/dashboard/components/get-premium-alert";
import { cn } from "@/lib/utils";
import ModalController from "@/components/modals/modal-controller";
import { OutletContainer } from "./components/outlet-container";
import { Skeleton } from "@/components/ui/skeleton";
import { AppErrorBoundary } from "@/components/errors/app-error-boundary";
import { useApp } from "@/app/providers/app-provider";
import Footer from "@/components/footer";

const DashboardSkeleton = () => {
	return (
		<div className="relative h-screen flex-col space-y-8 items-center justify-center p-8">
			<div className="space-y-2">
				<Skeleton className="h-16 w-full md:w-[250px]" />
				<Skeleton className="h-8 w-[200px]" />
			</div>
			<div className="flex flex-wrap gap-4">
				<Skeleton className="h-[125px] w-full md:w-[250px] rounded-xl" />
				<Skeleton className="h-[125px] w-full md:w-[250px] rounded-xl" />
				<Skeleton className="h-[125px] w-full md:w-[250px] rounded-xl" />
				<Skeleton className="h-[125px] w-full md:w-[250px] rounded-xl" />
				<Skeleton className="h-[125px] w-full md:w-[250px] rounded-xl" />
				<Skeleton className="h-[125px] w-full md:w-[250px] rounded-xl" />
			</div>
		</div>
	);
};

const Dashboard = () => {
	const isMobile = useIsMobile();
	const { currentTrack } = useAudio();
	const { appStore } = useApp();
	const isAuthLoading = appStore?.isAuthLoading ?? true;
	const session = appStore?.session ?? null;
	const showAuthSkeleton = isAuthLoading && session === null;
	const location = useLocation();
	const navigationType = useNavigationType();
	const mobileContentRef = useRef<HTMLDivElement | null>(null);
	const desktopContentRef = useRef<HTMLDivElement | null>(null);

	// Scroll to top only on forward navigation (PUSH), not on back/forward (POP)
	// biome-ignore lint/correctness/useExhaustiveDependencies: not sure why it doesn't like pathname
	useEffect(() => {
		if (navigationType === "PUSH") {
			if (mobileContentRef.current) {
				mobileContentRef.current.scrollTop = 0;
			}
			if (desktopContentRef.current) {
				desktopContentRef.current.scrollTop = 0;
			}
		}
	}, [location.pathname, navigationType]);

	return (
		<div className="relative">
			{isMobile ? (
				<div className="flex flex-col h-screen bg-background">
					<MobileNavbarTop />
					{/* Scrolling section */}
					{showAuthSkeleton ? (
						<DashboardSkeleton />
					) : (
						<section
							ref={mobileContentRef}
							className={cn(
								"overflow-y-scroll",
								currentTrack
									? "h-[calc(100svh-13rem)]"
									: "h-[calc(100svh-8rem)]",
							)}
						>
							<GetPremiumAlert />
							{/* Padding Container */}
							<main className={cn("p-4 pt-6 sm:p-8 flex flex-col min-h-full")}>
								{/* TODO: Might want to add an error boundary here */}
								<div className="flex-1">
									<Outlet />
								</div>
								<Footer />
							</main>
						</section>
					)}

					<AudioPlayer />
					<MobileNavbarBottom />
				</div>
			) : (
				<SidebarProvider>
					<AppSidebar />
					<SidebarInset
						// overflow required for embla slider
						className="relative w-full max-w-6xl overflow-x-hidden"
					>
						<div
							ref={desktopContentRef}
							className={cn(
								"mt-4 mr-4 bg-background rounded-lg overflow-y-scroll dark:border shadow",
								currentTrack
									? "h-[calc(100svh-8rem)]"
									: "h-[calc(100svh-2.25rem)]",
							)}
						>
							{showAuthSkeleton ? (
								<DashboardSkeleton />
							) : (
								<>
									<GetPremiumAlert />
									<OutletContainer className="flex flex-col min-h-full">
										<AppErrorBoundary className="flex flex-col flex-1">
											<div className="flex-1">
												<Outlet />
											</div>
											<Footer />
										</AppErrorBoundary>
									</OutletContainer>
								</>
							)}
						</div>
						<AudioPlayer />
					</SidebarInset>
				</SidebarProvider>
			)}
			<ModalController />
		</div>
	);
};

export default Dashboard;
